import api from "src/spintr/SpintrApi";

// The Google Picker / API JS libraries are loaded on demand from Google and
// attach themselves to the global scope. They have no bundled type definitions,
// so we describe them as `any` here (the SPA runs with a non-strict tsconfig).
declare const gapi: any;
declare const google: any;

interface IGooglePickerConfig {
    accessToken: string;
    appId: string;
    developerKey: string;
}

const GAPI_SCRIPT_SRC = "https://apis.google.com/js/api.js";

let gapiScriptPromise: Promise<void> | null = null;
let pickerLoadPromise: Promise<void> | null = null;

function loadGapiScript(): Promise<void> {
    if (gapiScriptPromise) {
        return gapiScriptPromise;
    }

    gapiScriptPromise = new Promise<void>((resolve, reject) => {
        if (typeof gapi !== "undefined") {
            resolve();
            return;
        }

        const existing = document.querySelector<HTMLScriptElement>(`script[src="${GAPI_SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () => reject(new Error("Failed to load the Google API script")));
            return;
        }

        const script = document.createElement("script");
        script.src = GAPI_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load the Google API script"));
        document.body.appendChild(script);
    });

    return gapiScriptPromise;
}

export function loadPickerApi(): Promise<void> {
    if (pickerLoadPromise) {
        return pickerLoadPromise;
    }

    pickerLoadPromise = loadGapiScript().then(
        () =>
            new Promise<void>((resolve, reject) => {
                gapi.load("picker", {
                    callback: () => resolve(),
                    onerror: () => reject(new Error("Failed to load the Google Picker API")),
                });
            })
    );

    return pickerLoadPromise;
}

interface IGooglePickerOptions {
    // Called once the picker has become visible.
    onOpened?: () => void;
    // Called when the picker is dismissed without a selection (cancelled).
    onCancelled?: () => void;
    // Allow selecting multiple files in the picker. Off by default; the file
    // browser opens one file at a time.
    multiSelect?: boolean;
}

/**
 * Uses the `drive.file` scope: the picker UI is hosted by Google, so the user
 * can browse their entire Drive, and selecting a file grants this app access to
 * just that file. `onPicked` is invoked with the selected files' ids and names
 * (a single-element array unless `options.multiSelect` is set).
 *
 * `options.onOpened` / `options.onCancelled` let callers react to the picker's
 * lifecycle (e.g. hide their own UI while the picker is open).
 */
export async function openGooglePicker(
    onPicked: (files: { id: string; name: string }[]) => void,
    options: IGooglePickerOptions = {}
): Promise<void> {
    const [config] = await Promise.all([
        api.get<IGooglePickerConfig>("/api/v1/files/google/picker-config").then((response) => response.data),
        loadPickerApi(),
    ]);

    if (!config || !config.accessToken) {
        return;
    }

    const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setEnableDrives(true);

    const builder = new google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(config.accessToken)
        .setOrigin(window.location.protocol + "//" + window.location.host)
        .setCallback((data: any) => {
            if (
                data.action === google.picker.Action.PICKED &&
                data.docs &&
                data.docs.length > 0
            ) {
                document.body.classList.remove("no-scroll");
                onPicked(data.docs.map((doc: any) => ({ id: doc.id, name: doc.name })));
            } else if (data.action === google.picker.Action.CANCEL ||
                data.action === google.picker.Action.ERROR
            ) {
                document.body.classList.remove("no-scroll");
                if (options.onCancelled) {
                    options.onCancelled();
                }
            }
        });

    if (options.multiSelect) {
        builder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
    }

    if (config.developerKey) {
        builder.setDeveloperKey(config.developerKey);
    }

    if (config.appId) {
        builder.setAppId(config.appId);
    }

    document.body.classList.add("no-scroll");
    builder.build().setVisible(true);

    if (options.onOpened) {
        options.onOpened();
    }
}
